import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { AppLogo } from '@/components/common/AppLogo';
import { PageTitle } from '@/components/layout/PageTitle';
import { filterNavSections, useNavAccess } from '@/components/layout/utils/nav.utils';
import { NAV_SECTIONS, type NavGroup } from '@/config/navigation';
import { APP_NAME } from '@/constants/app.constants';
import { useMenuLabels } from '@/features/menuLabels/hooks/useMenuLabels';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth.store';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { RAISED_CARD_CLASS } from '@/styles/raisedCard';
import { SOFT_BLUE_GRADIENT_CLASS } from '@/styles/softBlueSurface';
import { formatCount } from '@/utils/number';
import { TodayBadge } from './TodayBadge';

const HOME_TITLE = 'Home';
const AFTERNOON_START_HOUR = 12;
const EVENING_START_HOUR = 17;
const HAS_PAGES_LINE = 'Open one of your pages below, or use the menu on the left.';
const NO_PAGES_LINE = 'You do not have any pages yet. Ask your admin to give you access.';

function getGreeting(hour: number): string {
  if (hour < AFTERNOON_START_HOUR) return 'Good morning';
  if (hour < EVENING_START_HOUR) return 'Good afternoon';
  return 'Good evening';
}

interface PageShortcut {
  group: NavGroup;
  href: string;
}

function toPageShortcut(group: NavGroup): PageShortcut[] {
  const firstPage = group.children.find((child) => !child.upcoming);
  return firstPage === undefined ? [] : [{ group, href: firstPage.href }];
}

interface PageShortcutTileProps {
  shortcut: PageShortcut;
  label: string;
}

function PageShortcutTile({ shortcut, label }: PageShortcutTileProps) {
  const Icon = shortcut.group.icon;

  return (
    <Link
      to={shortcut.href as '/'}
      className={cn(
        RAISED_CARD_CLASS,
        'group flex items-center gap-3 rounded-2xl border bg-card p-4 transition-colors outline-none hover:border-primary/35 hover:bg-primary/3 focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-xl',
          ACCENT_MARK_CLASS,
        )}
      >
        <Icon className="size-5" aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">
          {formatCount(shortcut.group.children.length, 'page')}
        </span>
      </span>
      <ArrowRight
        className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary motion-reduce:transition-none"
        aria-hidden
      />
    </Link>
  );
}

export function WelcomePage() {
  const fullName = useAuthStore((state) => state.user?.fullName);
  const navAccess = useNavAccess();
  const { labelOf } = useMenuLabels();

  const shortcuts = filterNavSections(NAV_SECTIONS, '', navAccess, labelOf)
    .flatMap((section) => section.groups)
    .flatMap(toPageShortcut);
  const hasPages = shortcuts.length > 0;
  const greeting = getGreeting(new Date().getHours());

  return (
    <div className="flex flex-col gap-page-gap">
      <PageTitle titleAddon={<TodayBadge />}>{HOME_TITLE}</PageTitle>

      <section
        className={cn(
          'flex flex-col gap-5 rounded-2xl p-6 ring-1 ring-primary/15 sm:flex-row sm:items-center sm:p-8',
          SOFT_BLUE_GRADIENT_CLASS,
        )}
      >
        <span className="flex size-16 shrink-0 items-center justify-center rounded-2xl bg-card shadow-sm ring-1 ring-primary/15">
          <AppLogo className="size-10" />
        </span>
        <div className="flex min-w-0 flex-col gap-1.5">
          <p className="text-sm font-medium text-primary">Welcome to {APP_NAME}</p>
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">
            {fullName ? `${greeting}, ${fullName}` : greeting}
          </h2>
          <p className="text-sm text-muted-foreground">
            {hasPages ? HAS_PAGES_LINE : NO_PAGES_LINE}
          </p>
        </div>
      </section>

      {hasPages ? (
        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-foreground">Your pages</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {shortcuts.map((shortcut) => (
              <PageShortcutTile
                key={shortcut.group.key}
                shortcut={shortcut}
                label={labelOf(shortcut.group.key)}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
